///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Pure geometry for the earthquake cutout: how big the block is, which way
/// it points, how deep it goes and where its ticks fall. No engine types so
/// it stays trivially testable.
///

const cKmPerDegree = 111.32

/** One event of the sequence, positioned relative to the mainshock epicenter */
export interface SequenceEvent {
	id: string
	lat: number
	lon: number
	depthKm: number
	mag: number
	timeMs: number
	/** Member of the mainshock's DBSCAN cluster (vs. background seismicity) */
	inSequence: boolean
	/** Local equirectangular offsets from the mainshock epicenter (km) */
	east: number
	north: number
}

export interface CutoutLayout {
	/** Strike of the block's long (x) axis, radians clockwise from north, in [0, π) */
	strike: number
	/** Block center relative to the mainshock epicenter, local km east/north */
	centerEast: number
	centerNorth: number
	/** Block extent along strike (x) and across strike (z), km */
	lengthKm: number
	widthKm: number
	/** Bottom of the box below sea level, true km (a multiple of tickKm) */
	depthFloorKm: number
	/** Grid/tick spacing shared by the depth axis and the horizontal grid, true km */
	tickKm: number
	/**
	 * Vertical stretch of the depth axis (integer, printed on the wall). A
	 * shallow crustal sequence under a wide footprint is a slab a few km
	 * thick; stretching depth keeps the dip and the wall projections legible,
	 * like independent axis scales on any chart. 1 = true scale
	 */
	depthScale: number
	/** Clearance between the box bottom and the globe surface, km */
	hoverKm: number
}

/** Box depth in block-frame km (depth axis after stretching) */
export function boxDepthKm(layout: CutoutLayout): number {
	return layout.depthFloorKm * layout.depthScale
}

/**
 * Wells & Coppersmith (1994) subsurface rupture length for all slip types:
 * log10 L = 0.59 M − 2.44. M5 ≈ 3 km, M6 ≈ 13 km, M7 ≈ 50 km, M8 ≈ 190 km
 */
export function ruptureLengthKm(mag: number): number {
	return Math.pow(10, 0.59 * mag - 2.44)
}

/** Radius around the epicenter to pull candidate events from, km */
export function searchRadiusKm(mag: number): number {
	return clamp(3 * ruptureLengthKm(mag), 60, 500)
}

/**
 * DBSCAN neighbourhood for sequence membership, km. Scales with the
 * rupture so a great earthquake's aftershock zone stays one cluster, with
 * a floor above typical catalog location error and a cap so distant
 * background events don't chain in
 */
export function clusterEpsKm(mag: number): number {
	return clamp(0.5 * ruptureLengthKm(mag), 12, 60)
}

/** Nearest "nice" step (1-2-2.5-5 × 10^n) at or above x */
export function niceStep(x: number): number {
	if(x <= 0) return 1
	const exp = Math.floor(Math.log10(x))
	const base = Math.pow(10, exp)
	const n = x / base
	const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10
	return step * base
}

/** Local km east/north of (lat, lon) from the origin (lat0, lon0), equirectangular */
export function localOffsetKm(lat: number, lon: number, lat0: number, lon0: number): { east: number; north: number } {
	let dLon = lon - lon0
	if(dLon > 180) dLon -= 360
	if(dLon < -180) dLon += 360
	return {
		east: dLon * cKmPerDegree * Math.cos(lat0 * Math.PI / 180),
		north: (lat - lat0) * cKmPerDegree
	}
}

/** Inverse of localOffsetKm */
export function localToLatLon(east: number, north: number, lat0: number, lon0: number): { lat: number; lon: number } {
	return {
		lat: lat0 + north / cKmPerDegree,
		lon: lon0 + east / (cKmPerDegree * Math.cos(lat0 * Math.PI / 180))
	}
}

/**
 * Rotate local east/north into the block frame: x along strike, z across
 * strike (strike rotated 90° clockwise, so strike north → z east). With
 * y up this is right-handed: x × y = z
 */
export function toBlockXZ(east: number, north: number, strike: number): { x: number; z: number } {
	const s = Math.sin(strike)
	const c = Math.cos(strike)
	return {
		x: east * s + north * c,
		z: east * c - north * s
	}
}

/** Inverse of toBlockXZ */
export function fromBlockXZ(x: number, z: number, strike: number): { east: number; north: number } {
	const s = Math.sin(strike)
	const c = Math.cos(strike)
	return {
		east: x * s + z * c,
		north: x * c - z * s
	}
}

/** Horizontal principal axes of the sequence (map view) */
export interface SequenceTrend {
	/** Azimuth of the major axis, radians clockwise from north in [0, π) */
	strike: number
	/** Standard deviations along the major and minor axes, km */
	sigmaMajor: number
	sigmaMinor: number
	/** Extent of the cloud along each axis (max - min of projections), km */
	lengthKm: number
	widthKm: number
	/** Midpoint of the extents, local km east/north of the mainshock */
	centerEast: number
	centerNorth: number
	n: number
}

/**
 * Principal axes of the cluster's epicenters. The major axis is the
 * sequence's trend; sigmaMinor / sigmaMajor near 1 means a round cloud
 * whose "trend" is noise
 */
export function sequenceTrend(events: SequenceEvent[]): SequenceTrend | undefined {
	const pts = events.filter(e => e.inSequence)
	const n = pts.length
	if(n < 3) return undefined
	let me = 0, mn = 0
	for(const p of pts) { me += p.east / n; mn += p.north / n }
	let see = 0, snn = 0, sen = 0
	for(const p of pts) {
		const de = p.east - me, dn = p.north - mn
		see += de * de / n
		snn += dn * dn / n
		sen += de * dn / n
	}
	// 2x2 symmetric eigen decomposition
	const tr = see + snn
	const det = see * snn - sen * sen
	const disc = Math.sqrt(Math.max(tr * tr / 4 - det, 0))
	const l1 = tr / 2 + disc
	const l2 = Math.max(tr / 2 - disc, 0)
	// Major eigenvector
	let ve: number, vn: number
	if(Math.abs(sen) > 1e-9) {
		ve = l1 - snn
		vn = sen
	} else if(see >= snn) {
		ve = 1; vn = 0
	} else {
		ve = 0; vn = 1
	}
	const len = Math.hypot(ve, vn) || 1
	ve /= len
	vn /= len
	let strike = Math.atan2(ve, vn)
	strike = ((strike % Math.PI) + Math.PI) % Math.PI
	// Extents along the axes
	let minL = Infinity, maxL = -Infinity, minW = Infinity, maxW = -Infinity
	const we = vn, wn = -ve
	for(const p of pts) {
		const l = p.east * ve + p.north * vn
		const w = p.east * we + p.north * wn
		minL = Math.min(minL, l); maxL = Math.max(maxL, l)
		minW = Math.min(minW, w); maxW = Math.max(maxW, w)
	}
	const midL = (minL + maxL) / 2
	const midW = (minW + maxW) / 2
	return {
		strike,
		sigmaMajor: Math.sqrt(l1),
		sigmaMinor: Math.sqrt(l2),
		lengthKm: maxL - minL,
		widthKm: maxW - minW,
		centerEast: midL * ve + midW * we,
		centerNorth: midL * vn + midW * wn,
		n
	}
}

/**
 * Size and orient the block around a sequence. The long axis follows the
 * principal axis of the cluster's epicenters (its trend), the footprint is
 * the cluster's extent with padding, never smaller than a few rupture
 * lengths so a lone mainshock still gets a readable slab; aspect is kept
 * between 1:1 and ~2:1 so the walls stay chart-like
 */
export function buildLayout(mainshockMag: number, events: SequenceEvent[]): CutoutLayout {
	const rupture = ruptureLengthKm(mainshockMag)
	const trend = sequenceTrend(events)

	let strike = trend?.strike ?? 0
	let centerEast = trend?.centerEast ?? 0
	let centerNorth = trend?.centerNorth ?? 0
	let envLength = trend?.lengthKm ?? 0
	let envWidth = trend?.widthKm ?? 0

	// A tiny cloud (two or three co-located aftershocks) carries no
	// strike information; fall back to north-up centered on the mainshock
	if(!trend || envLength < Math.max(4, rupture * 0.5)) {
		strike = 0
		centerEast = 0
		centerNorth = 0
		envLength = 0
		envWidth = 0
	}

	// The floor is a tick multiple a little below the deepest sequence event
	let maxDepth = 0
	for(const e of events) {
		if(e.inSequence) maxDepth = Math.max(maxDepth, e.depthKm)
	}
	const rawFloor = Math.max(20, maxDepth * 1.15 + 5)
	const tickKm = niceStep(rawFloor / 5)
	const depthFloorKm = Math.ceil(rawFloor / tickKm - 1e-6) * tickKm

	// Footprint: padded envelope, at least a few rupture lengths, and never a
	// needle (a deep-focus event at 500 km still gets a column half as wide)
	const lengthKm = clamp(Math.max(envLength * 1.4, 2.5 * rupture, 0.5 * depthFloorKm, 30), 30, 900)
	const widthKm = clamp(Math.max(envWidth * 1.4, 0.55 * lengthKm), 0.55 * lengthKm, lengthKm)

	// Stretch depth until the box is ~30% as deep as it is long, so a thin
	// crustal slab doesn't squash its own cross-section; deep boxes stretch less
	const maxScale = depthFloorKm > 60 ? 2 : 4
	const depthScale = clamp(Math.round(0.3 * lengthKm / depthFloorKm), 1, maxScale)

	const hoverKm = clamp(0.08 * lengthKm, 8, 40)

	return { strike, centerEast, centerNorth, lengthKm, widthKm, depthFloorKm, tickKm, depthScale, hoverKm }
}

/** True when a block-frame point lies inside the footprint */
export function insideFootprint(layout: CutoutLayout, x: number, z: number, marginKm: number = 0): boolean {
	return Math.abs(x) <= layout.lengthKm / 2 + marginKm && Math.abs(z) <= layout.widthKm / 2 + marginKm
}

/**
 * Vertical exaggeration for the terrain lid: enough that the relief inside
 * the footprint (summit minus lowest land) reads as ~5% of the block
 * length, but never lifting the summit above 20% of it (a high plateau is
 * tall before any stretching). Rounded so the printed factor is simple
 */
export function terrainExaggeration(layout: CutoutLayout, maxElevKm: number, minLandElevKm: number = 0): number {
	const relief = Math.max(maxElevKm - minLandElevKm, 0.3)
	const raw = 0.05 * layout.lengthKm / relief
	const summitCap = 0.2 * layout.lengthKm / Math.max(maxElevKm, 0.3)
	return Math.max(1, Math.round(clamp(Math.min(raw, summitCap), 2, 20)))
}

/**
 * Exaggeration below sea level: the land factor, capped so the deepest sea
 * floor in the footprint stays within the top quarter of the box (the
 * shallow hypocenters live right under the sea floor; a trench stretched
 * past them would put the crust below the earthquakes in it). Never below 1
 */
export function seaFloorExaggeration(layout: CutoutLayout, landExaggeration: number, maxDepthKm: number): number {
	if(maxDepthKm <= 0.05) return landExaggeration
	const cap = 0.25 * boxDepthKm(layout) / maxDepthKm
	return Math.max(1, Math.floor(Math.min(landExaggeration, cap)))
}

/**
 * Hypocenter sphere radius, km. Scales with the block so a sequence reads
 * the same at any footprint, and exponentially with magnitude so the
 * mainshock dominates without swallowing its aftershocks
 */
export function hypocenterRadiusKm(layout: CutoutLayout, mag: number): number {
	const base = 0.0055 * Math.max(layout.lengthKm, boxDepthKm(layout))
	return clamp(base * Math.pow(1.45, mag - 4), base * 0.45, base * 5)
}

export function clamp(v: number, lo: number, hi: number): number {
	return Math.min(hi, Math.max(lo, v))
}
